/**
 * SeatPanel — the selected seat, as an argument/evidence split (K3 direction):
 * a LEFT column argues (eyebrow → DM Serif headline → capabilities as a RULED
 * LIST, not cards → PROOF as one mono line → square mono CTA) and a RIGHT column
 * proves (the SeatPeek exhibit). 5/7 grid at lg; on mobile the exhibit sits right
 * under the headline so product proof comes early. Token-only (design law).
 *
 * Orange budget (K3, E2): the ACTIVE TAB already carries seat identity in orange,
 * so the in-column eyebrow is MUTED, not orange — redundant labeling is not
 * emphasis. The three orange atoms per seat view are: the active tab, the
 * exhibit's decisive figure (the number), and the primary CTA (the ask). The
 * "Open the workspace" deep-link is a cream underlined link, never orange and
 * never arrowed (the arrow is reserved for the one primary CTA).
 */

import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SEAT_PANEL_ID, seatTabId } from './SeatSelector';
import SeatPeek from './SeatPeek';
import { getSeatPeek } from './seatPeeks';
import type { Seat } from '../../config/seats.config';

const HAIR = { borderColor: 'var(--color-hairline)' } as const;

function Capabilities({ seat }: { seat: Seat }) {
  return (
    <ul className="mt-8 border-t" style={HAIR}>
      {seat.capabilities.map((cap, i) => (
        <li key={cap} className="flex gap-4 border-b py-4" style={HAIR}>
          <span className="font-mono text-xs leading-relaxed text-text-dim">{String(i + 1).padStart(2, '0')}</span>
          <span className="font-body text-sm leading-relaxed text-text-secondary">{cap}</span>
        </li>
      ))}
    </ul>
  );
}

function Proof({ seat }: { seat: Seat }) {
  return (
    <p className="mt-6 font-mono text-[11px] leading-relaxed">
      <span className="uppercase tracking-[0.16em] text-text-secondary">Proof</span>{' '}
      <span className="text-text-muted">— {seat.proof}</span>
    </p>
  );
}

function Actions({ seat }: { seat: Seat }) {
  return (
    <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
      {seat.marketingCta ? (
        <Link
          to={seat.marketingCta.href}
          className="inline-flex shrink-0 items-center justify-center gap-3 whitespace-nowrap rounded-none bg-brand px-7 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.14em] transition-colors hover:bg-brand-hover"
          style={{ color: 'var(--color-on-brand)' }}
        >
          {seat.marketingCta.label}
          <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
        </Link>
      ) : (
        seat.ctaNote && <p className="max-w-sm font-body text-sm leading-relaxed text-text-muted">{seat.ctaNote}</p>
      )}

      {/* Deep-link into the seat's REAL dashboard (SSOT: seats.config.dashboardRoute).
          Secondary-action species: cream mono underline, NOT orange, no arrow
          (the arrow is reserved for the one primary CTA). */}
      {seat.dashboardRoute && (
        <Link
          to={seat.dashboardRoute}
          className="inline-flex shrink-0 whitespace-nowrap font-mono text-xs uppercase tracking-[0.12em] text-text-secondary underline decoration-hairline underline-offset-4 transition-colors hover:text-text-primary"
        >
          Open the workspace
        </Link>
      )}
    </div>
  );
}

export default function SeatPanel({ seat }: { seat: Seat }) {
  const peek = getSeatPeek(seat.id);

  const Eyebrow = (
    <p className="font-mono text-xs uppercase tracking-[0.22em] text-text-dim">{seat.label}</p>
  );
  const Tagline = <p className="mt-2 font-body text-sm text-text-muted">{seat.tagline}</p>;
  const Headline = (
    <h3 className="mt-6 font-heading text-3xl leading-[1.08] tracking-tight text-text-primary md:text-[2.5rem]">
      {seat.job}
    </h3>
  );

  // No exhibit (client) → single column, no empty right half.
  if (!peek) {
    return (
      <div role="tabpanel" id={SEAT_PANEL_ID} aria-labelledby={seatTabId(seat.id)} className="mt-10 max-w-2xl lg:mt-14">
        {Eyebrow}
        {Tagline}
        {Headline}
        <Capabilities seat={seat} />
        <Proof seat={seat} />
        <Actions seat={seat} />
      </div>
    );
  }

  return (
    <div
      role="tabpanel"
      id={SEAT_PANEL_ID}
      aria-labelledby={seatTabId(seat.id)}
      className="mt-10 lg:mt-14 lg:grid lg:grid-cols-12 lg:gap-x-10 lg:gap-y-6"
    >
      {/* Argument — header */}
      <div className="lg:col-span-5 lg:col-start-1 lg:row-start-1">
        {Eyebrow}
        {Tagline}
        {Headline}
      </div>

      {/* Evidence — the exhibit (right on lg, directly under the headline on mobile) */}
      <div className="mt-8 lg:col-span-7 lg:col-start-6 lg:row-span-2 lg:row-start-1 lg:mt-0">
        <SeatPeek peek={peek} seatLabel={seat.label} />
        <p
          className="mt-4 border-t pt-3 font-mono text-[10px] uppercase tracking-[0.1em] leading-relaxed text-text-dim"
          style={HAIR}
        >
          {peek.figCaption}
        </p>
      </div>

      {/* Argument — body */}
      <div className="mt-2 lg:col-span-5 lg:col-start-1 lg:row-start-2 lg:mt-0">
        <Capabilities seat={seat} />
        <Proof seat={seat} />
        <Actions seat={seat} />
      </div>
    </div>
  );
}
